import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FolderTabInfos } from '@hub4b/models/folder-tab-infos';
import { FoldersListConfiguration } from '@hub4b/models/folders-list-configuration';
import { PredefinedFilter } from '@hub4b/models/predefined-filter';
import { AttributeDefinition } from '@hub4b/models/server/attribute-definition';
import { ColumnDefinition } from '@hub4b/models/server/column-definition';
import { ViewType } from '@hub4b/models/server/enums/view-type';
import { ListLayout } from '@hub4b/models/server/list-layout';
import { View } from '@hub4b/models/server/view';
import { NotificationService } from '@hub4b/services/actions/notification.service';
import { SpinnerService } from '@hub4b/services/core/spinner.service';
import { FolderCounterEventsService } from '@hub4b/services/events/folder-counter-events.service';
import { AttributeDefinitionsService } from '@hub4b/services/http/attribute-definitions.service';
import { ListLayoutsService } from '@hub4b/services/http/list-layouts.service';
import { PredefinedFiltersService } from '@hub4b/services/http/predefined-filters.service';
import { UsersService } from '@hub4b/services/http/users.service';
import { AttributeDefinitionsStateService } from '@hub4b/services/state/attribute-definitions-state.service';
import { ConnectedUsersStateService } from '@hub4b/services/state/connected-users-state.service';
import { FolderSearchStateService } from '@hub4b/services/state/folder-search-state.service';
import { ProfileSettingsStateService } from '@hub4b/services/state/profile-settings-state.service';
import { ViewsStateService } from '@hub4b/services/state/views-state.service';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Observable, Subject, forkJoin, of, takeUntil } from 'rxjs';
import { HttpError } from 'src/app/core/http-error';
import { FoldersListBoxesSelectorContainerComponent } from './folders-list-boxes-selector-container/folders-list-boxes-selector-container.component';
import { FoldersListDatasComponent } from './folders-list-datas/folders-list-datas.component';
import { FoldersListHeaderComponent } from './folders-list-header/folders-list-header.component';
import { ViewSelectorComponent } from './view-selector/view-selector.component';

@Component({
    selector: 'app-folders-list',
    templateUrl: './folders-list.component.html',
    styleUrls: ['./folders-list.component.scss'],
    imports: [FoldersListBoxesSelectorContainerComponent, FoldersListHeaderComponent, FoldersListDatasComponent, NgxSpinnerModule, ViewSelectorComponent],
    providers: [FolderSearchStateService, FolderCounterEventsService]
})
export class FoldersListComponent implements OnInit, OnDestroy {
    @ViewChild(FoldersListDatasComponent) listDataComponent: FoldersListDatasComponent;

    /**
     * Utilisé pour emettre un evenement vers le composant parent lorsque l'utilisateur consulte ou édite un dossier.
     */
    @Output()
    folderDisplayed: EventEmitter<FolderTabInfos> = new EventEmitter();

    /**
     * Vue actuellement selectionnée.
     */
    @Input()
    public view: View = null;

    /**
     * Vue selectionné par défaut.
     */
    public defaultView: View = null;

    /**
     * Utilisé pour gérer la désincription des observables lorsque le composant est détruit.
     */
    protected componentDestroyed$: Subject<void> = new Subject<void>();

    /**
     * Configuration de la liste des dossiers.
     */
    public configuration: FoldersListConfiguration = null;

    /**
     * Constructeur.
     * @param viewsStateService : Service gérant l'état de la vue selectionnée.
     * @param attributeDefinitionsStateService : Service gérant l'état des definitions d'attributs.
     * @param attributeService : Service de gestion des attributs.
     * @param spinner : Service gérant l'affichage du spinner.
     * @param profileSettingsStateService : Service gérant l'état du paramétrage du profile.
     * @param listLayoutsService : Service de gestion des mises en page.
     * @param usersService : Service de gestion des données utilisateurs.
     * @param connectedUsersStateService : Service de gestion de l'utilisateur connecté.
     * @param predefinedFiltersService : Service de gestion des filtres prédéfinis.
     * @param notificationService  : Service de notification.
     */
    constructor(
        private viewsStateService: ViewsStateService,
        private attributeDefinitionsStateService: AttributeDefinitionsStateService,
        private attributeService: AttributeDefinitionsService,
        protected spinner: SpinnerService,
        private profileSettingsStateService: ProfileSettingsStateService,
        private listLayoutsService: ListLayoutsService,
        private usersService: UsersService,
        private connectedUsersStateService: ConnectedUsersStateService,
        private predefinedFiltersService: PredefinedFiltersService,
        private notificationService: NotificationService
    ) {
        this.loadAccessibleViews();

        this.profileSettingsStateService.onProfileSettingsChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((p) => {
            if (p) {
                if (p.defaultViewId) {
                    this.defaultView = this.viewsStateService.workflowViews.find((v) => v.id === p.defaultViewId);
                } else {
                    this.defaultView = null;
                }
            }
        });
    }

    /**
     * Appelé à l'initialisation du composant .
     */
    ngOnInit(): void {
        this.subscribeToViewSelectionChangeEvent();
    }

    /**
     * Appelé à la destruction du composant.
     */
    ngOnDestroy(): void {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    /**
     * Appelé lorsque l'utilisateur consulte ou édite un dossier.
     * @param folderInfos : Informations sur le dossier
     */
    public onFolderDisplayed(folderInfos: FolderTabInfos) {
        this.folderDisplayed.emit(folderInfos);
    }

    public onViewSelected(view: View) {
        this.viewsStateService.setSelectedView(view);
    }

    /**
     * S'abonne aux événements de changement de vue sélectionnée.
     */
    private subscribeToViewSelectionChangeEvent() {
        this.viewsStateService.onViewsSelectionChange$.pipe(takeUntil(this.componentDestroyed$)).subscribe((x) => {
            if (!!x && !!x.currentView) {
                this.view = x.currentView;
                this.loadViewConfiguration(x.currentView);
            }
        });
    }

    /**
     * Charge la configuration de la vue.
     */
    protected loadViewConfiguration(view: View) {
        this.spinner.show('folders-container-spinner');

        forkJoin([this.getListLayout(), this.getColumnDefinitions(), this.getAttributesDefinitions(view.id), this.getPredefinedFilters(view.id), this.getDefaultFilter(view.id)])
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe({
                next: ([listLayout, columnDefinitions, attributesDefinitions, predefinedFilters, defaultFilter]) => {
                    this.configuration = new FoldersListConfiguration(
                        view.id,
                        view.viewType,
                        listLayout,
                        columnDefinitions,
                        attributesDefinitions,
                        predefinedFilters,
                        defaultFilter
                    );
                    this.attributeDefinitionsStateService.setAttributeDefinitions(view.id, attributesDefinitions);
                    this.spinner.hide('folders-container-spinner');
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError(error.reasonPhrase, `La vue n'a pas pu être chargée : ${error.message}`);
                    this.spinner.hide('folders-container-spinner');
                },
            });
    }

    public getListLayout(): Observable<ListLayout> {
        let selectedView = this.viewsStateService.getSelectedView();

        if (!!selectedView) {
            let userDefaultLayoutId: number = null;

            if (this.profileSettingsStateService.getProfileSettings().viewSettings.get(selectedView.id)) {
                userDefaultLayoutId = this.profileSettingsStateService.getProfileSettings().viewSettings.get(selectedView.id).defaultViewLayout;
            }

            if (!!userDefaultLayoutId) {
                // Récupération de la mise en page spécifiée dans le paramétrage du profil
                return this.listLayoutsService.get(userDefaultLayoutId);
            } else {
                // Récupération de la mise en page assigné à l'utilisateur.
                return this.usersService.getViewListLayout(selectedView.id);
            }
        } else {
            return of(null);
        }
    }

    public getColumnDefinitions(): Observable<ColumnDefinition[]> {
        let selectedView = this.viewsStateService.getSelectedView();

        if (!!selectedView) {
            return this.usersService.getViewColumnsDefinitions(selectedView.id);
        } else {
            return of([]);
        }
    }

    /**
     * Restaure la position de la barre de défilement (Utilisé lorsque l'on revient sur l'onglet de la liste après en avoir selectionné un autre). Sinon la barre de défilement est remise en haut.
     */
    public restoreScrollPosition() {
        this.listDataComponent.restoreScrollPosition();
    }

    /**
     * Récupère la définition des attributs des dossiers.
     */
    private getAttributesDefinitions(viewId: number): Observable<AttributeDefinition[]> {
        return this.attributeService.List(viewId);
    }

    /**
     * Récupère les filtres prédéfinis de la vue.
     */
    private getPredefinedFilters(viewId: number): Observable<PredefinedFilter[]> {
        return this.predefinedFiltersService.List(viewId);
    }

    private getDefaultFilter(viewId: number): Observable<PredefinedFilter> {
        if (this.profileSettingsStateService.getProfileSettings().autoFiltersSaveAndRestore === true) {
            // Restauration auto des filtres si l'utilisateur l'a activé dans ses paramètres de profil.
            let localStorageValue = localStorage.getItem(this.getLocalStorageKey(viewId));

            if (localStorageValue) {
                return of(PredefinedFilter.from(JSON.parse(localStorageValue)));
            } else {
                let autoFilter = new PredefinedFilter();
                autoFilter.viewId = viewId;
                return of(autoFilter);
            }
        } else if (
            this.profileSettingsStateService.getProfileSettings().viewSettings.get(viewId) &&
            this.profileSettingsStateService.getProfileSettings().viewSettings.get(viewId).defaultViewPredefinedFilter
        ) {
            const predefinedFilterId = this.profileSettingsStateService.getProfileSettings().viewSettings.get(viewId).defaultViewPredefinedFilter;
            return this.predefinedFiltersService.get(predefinedFilterId);
        } else {
            let predefinedFilter = new PredefinedFilter();
            predefinedFilter.viewId = viewId;
            return of(predefinedFilter);
        }
    }

    /**
     * Récupère la clé utilisée pour stockée le filtre par défaut d'une vue.
     * @param viewId : Id de la vue.
     * @returns Clé pour stockée le contexte.
     */
    private getLocalStorageKey(viewId: number) {
        return `folderListContext-${this.connectedUsersStateService.getUserConnected().userId}-${viewId}`;
    }

    private loadAccessibleViews() {
        this.usersService.getViews().subscribe((x) => {
            this.viewsStateService.boxViews = x.filter((v) => v.viewType === ViewType.Box).sort((v1, v2) => v1.name.localeCompare(v2.name));
            this.viewsStateService.workflowViews = x.filter((v) => v.viewType === ViewType.Workflow).sort((v1, v2) => v1.name.localeCompare(v2.name));
            this.viewsStateService.archivingViews = x
                .filter((v) => v.viewType === ViewType.ArchivingBox || v.viewType === ViewType.ArchivingWorkflow)
                .sort((v1, v2) => v1.name.localeCompare(v2.name));

            // Recuperation de la vue par défaut depuis les paramètres de l'utilisateur.
            var defaultViewId = this.profileSettingsStateService.getProfileSettings()?.defaultViewId;

            if (defaultViewId) {
                this.defaultView = x.find((x) => x.id === defaultViewId);
            } else {
                this.defaultView = null;
            }
        });
    }
}
